'use client'
import React from 'react'
import { useTranslations } from '@/lib/next-shim'

import { Box, Paper, Stack, Typography } from '@mui/material'

import CheckboxInputController from '@/components/ui/inputs/CheckboxInputController'
import TextInputController from '@/components/ui/inputs/TextInputController'
import SelectInputController from '@/components/ui/inputs/SelectInputController'
import DateInput from '@/components/ui/forms/DateInput'
import { LoadingButton } from '@/components/external/material'
import { Link } from '@/lib/next-shim'
import { useGetEstablishments } from '@/shared/model/useGetEstablishments'

import { DefaultRegisterFormData, useDefaultRegisterForm } from '../model/defaultRegisterForm'
import { SubmitHandler } from 'react-hook-form'

type RegisterFormProps = {
  isLoading?: boolean
  onSubmit: SubmitHandler<DefaultRegisterFormData>
}

export function DefaultRegisterForm({ isLoading = false, onSubmit }: RegisterFormProps) {
  const {
    control,
    formState: { isValid },
    handleSubmit,
  } = useDefaultRegisterForm()
  const t = useTranslations()
  const { establishments } = useGetEstablishments()

  return (
    <Stack component="form" spacing={2.5} onSubmit={handleSubmit(onSubmit)} noValidate>
      <TextInputController control={control} fullWidth label={t('field.email.label')} name="email" required />
      <TextInputController control={control} fullWidth label={t('field.name.label')} name="name" required />
      <TextInputController control={control} fullWidth label={t('field.surname.label')} name="surname" required />

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', borderColor: 'grey.200', borderRadius: 2 }}>
        <Stack spacing={2}>
          <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: '0.08em' }}>
            {t('RegisterForm.insSection')}
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextInputController control={control} fullWidth label={t('field.birthName.label')} name="birthName" required />
            <TextInputController control={control} fullWidth label={t('field.birthFirstNames.label')} name="birthFirstNames" required />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <DateInput<DefaultRegisterFormData>
              control={control}
              colSpan="flex flex-col"
              id="birthDate"
              label={t('field.birthDate.label')}
              disableFuture
              required
            />
            <SelectInputController
              control={control}
              fullWidth
              label={t('field.sex.label')}
              name="sex"
              options={[
                { name: t('enum.Gender.M'), value: 'M' },
                { name: t('enum.Gender.F'), value: 'F' },
              ]}
              required
            />
          </Box>

          <TextInputController control={control} fullWidth label={t('field.birthPlace.label')} name="birthPlace" required />
        </Stack>
      </Paper>

      <SelectInputController
        control={control}
        fullWidth
        label={t('field.establishment.label')}
        name="establishment"
        options={[{ name: t('field.establishment.placeholder'), value: '' }, ...establishments.map((est) => ({ name: est.name, value: est._id }))]}
      />

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <TextInputController control={control} fullWidth label={t('field.phone.label')} name="phone" />
        </Box>
        <Box sx={{ flex: 1 }}>
          <TextInputController control={control} fullWidth label={t('field.secu.label')} name="secu" />
        </Box>
      </Box>

      <TextInputController control={control} fullWidth label={t('field.password.label')} name="password" TextInputProps={{ type: 'password' }} required />
      <TextInputController
        control={control}
        fullWidth
        label={t('field.confirmPassword.label')}
        name="confirmPassword"
        TextInputProps={{ type: 'password' }}
        required
      />

      <CheckboxInputController
        control={control}
        label={
          <Typography variant="body2" color="text.secondary">
            {t.rich('page.Register.signUpForm.field.termsAndConditions.label', {
              link: (chunks: React.ReactNode) => (
                <Link href="/terms-and-conditions" target="_blank" style={{ color: 'inherit', fontWeight: 500, textDecoration: 'underline' }}>
                  {chunks}
                </Link>
              ),
            })}
          </Typography>
        }
        name="terms"
      />

      <LoadingButton
        disabled={!isValid}
        disableElevation
        loading={isLoading}
        type="submit"
        variant="contained"
        size="large"
        fullWidth
        sx={{ py: 1.5, fontWeight: 500, borderRadius: 2 }}
      >
        {t('button.signUp.label')}
      </LoadingButton>
    </Stack>
  )
}
